import React, { useEffect, useState } from 'react';
import { getFormatter, getTranslatedLabel } from '../StatsFormatters';
import { format } from 'date-fns';
import { fetchChartData, fetchClientsData, fetchClientDisputesData } from '../../services/api';
import './statsTable.css';
import StatsClientFilterForm from '../StatsClientFilterForm/StatsClientFilterForm';

const StatsTable = ({ data_type, startDate, endDate, timeFrame, other }) => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStatsClientFilterForm, setShowStatsClientFilterForm] = useState(false);

  useEffect(() => {
    const getChartData = async () => {
      try {
        setLoading(true);
        setError(null);
        const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : null;
        const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : null;
        const fetchedData = await fetchChartData(formattedStartDate, formattedEndDate, timeFrame);
        setTableData(fetchedData);
      } catch (error) {
        setError('Erreur lors de la récupération des données');
      } finally {
        setLoading(false);
      }
    };

    const getClientsData = async () => {
      try {
        setLoading(true);
        setError(null);
        const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : null;
        const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : null;
        let fetchedData = await fetchClientsData(
          formattedStartDate,
          formattedEndDate,
          other === 'grouped-clients'
        );
        fetchedData = fetchedData.map(client => ({
          ...client,
          selected: true,
        }));
        setTableData(fetchedData);
      } catch (error) {
        setError('Erreur lors de la récupération des données clients');
      } finally {
        setLoading(false);
      }
    };

    const getClientDisputesData = async () => {
      try {
        setLoading(true);
        setError(null);
        const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : null;
        const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : null;
        const fetchedData = await fetchClientDisputesData(formattedStartDate, formattedEndDate);
        setTableData(fetchedData);
      } catch (error) {
        setError('Erreur lors de la récupération des données litiges');
      } finally {
        setLoading(false);
      }
    };

    if (data_type === 'litiges') {
      getClientDisputesData();
    } else if (data_type === 'clients') {
      getClientsData();
    } else {
      getChartData();
    }
  }, [data_type, timeFrame, startDate, endDate, other]);

  const renderTableHeaders = () => {
    if (tableData.length === 0) return null;
    return Object.keys(tableData[0]).map(
      key =>
        key !== 'selected' && (
          <th key={key} className="filter-header">
            {key === 'date' ? (
              getTranslatedLabel(timeFrame)
            ) : key === 'client' && data_type === 'clients' && other === 'grouped-clients' ? (
              <div className="filter-header-div">{getTranslatedLabel('client_group')}</div>
            ) : key === 'client' && data_type === 'clients' ? (
              <div className="filter-header-div">
                {getTranslatedLabel('client')}
                <button
                  type="button"
                  className="filter-button"
                  onClick={() => setShowStatsClientFilterForm(!showStatsClientFilterForm)}
                >
                  Filtrer
                </button>
              </div>
            ) : (
              getTranslatedLabel(key)
            )}
            {key === 'client' && data_type === 'clients' && showStatsClientFilterForm && (
              <StatsClientFilterForm
                clients={tableData}
                setClients={setTableData}
                closeForm={() => setShowStatsClientFilterForm(false)}
              />
            )}
          </th>
        )
    );
  };

  const renderTableRows = () => {
    if (tableData.length === 0) return null;

    let lastYear = null;
    const rows = [];

    tableData.forEach((row, index) => {
      if ((data_type === 'clients' && row.selected) || data_type !== 'clients') {
        const dateKey = 'date';
        const date = new Date(row[dateKey]);

        if (!isNaN(date)) {
          const year = date.getFullYear();

          if (
            data_type !== 'clients' &&
            timeFrame === 'month' &&
            lastYear !== null &&
            lastYear !== year
          ) {
            const totalYearRow = calculateYearTotal(tableData.slice(0, index), lastYear);
            rows.push(
              <tr key={`total-${lastYear}`} className="bold-row">
                <td>Total {lastYear}</td>
                {Object.keys(row)
                  .filter(key => key !== dateKey)
                  .map(key => (
                    <td key={key}>{getFormatter(key)(totalYearRow[key])}</td>
                  ))}
              </tr>
            );
          }
          lastYear = year;
        }

        rows.push(
          <tr key={index}>
            {Object.keys(row).map(
              key =>
                key !== 'selected' && (
                  <td key={key}>
                    {key !== 'date'
                      ? getFormatter(key)(row[key])
                      : getFormatter(timeFrame)(row[key])}
                  </td>
                )
            )}
          </tr>
        );
      }
    });

    if (data_type !== 'clients' && timeFrame === 'month' && lastYear !== null) {
      const totalYearRow = calculateYearTotal(tableData, lastYear);
      rows.push(
        <tr key={`total-${lastYear}`} className="bold-row">
          <td>Total {lastYear}</td>
          {Object.keys(tableData[0])
            .filter(key => key !== 'date')
            .map(key => (
              <td key={key}>{getFormatter(key)(totalYearRow[key])}</td>
            ))}
        </tr>
      );
    }

    return rows;
  };

  const calculateYearTotal = (data, year) => {
    const filteredData = data.filter(row => new Date(row.date).getFullYear() === year);
    const totalRow = {};

    filteredData.forEach(row => {
      Object.keys(row).forEach(key => {
        if (!totalRow[key]) totalRow[key] = 0;
        if (typeof row[key] === 'number' && !key.includes('rate')) {
          totalRow[key] += row[key];
        }
      });
    });

    try {
      if (totalRow['total_carrier_price'] !== 0) {
        totalRow['margin_rate'] = (totalRow['margin'] / totalRow['total_carrier_price']) * 100;
      } else {
        totalRow['margin_rate'] = 100;
      }
    } catch {
      // nothing changes
    }

    return totalRow;
  };

  if (loading) return <p>Chargement...</p>;
  if (error) return <p>{error}</p>;

  return (
    <table className="stats-table">
      <thead>
        <tr>{renderTableHeaders()}</tr>
      </thead>
      <tbody>{renderTableRows()}</tbody>
    </table>
  );
};

export default StatsTable;
